import React, { useEffect, useRef } from "react";

function snippet(text, max = 400) {
  const value = (text || "").trim();
  if (!value) return "";
  return value.length > max ? `${value.slice(0, max).trimEnd()}…` : value;
}

export default function RfpEvidenceDrawer({ rowId, unit, reviewRowId, onClose, onOpenInReview }) {
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!rowId) return null;

  const displayText = snippet(unit?.text) || unit?.text || "";

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-black/40" onClick={onClose}>
      <div
        ref={ref}
        className="flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="font-semibold">Evidence · {rowId}</h3>
          <button type="button" className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 text-sm">
          {unit ? (
            <dl className="space-y-3">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Unit ID</dt>
                <dd className="font-mono">{unit.id || rowId}</dd>
              </div>
              {unit.source_row_id ? (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Review row</dt>
                  <dd className="font-mono">{unit.source_row_id}</dd>
                </div>
              ) : null}
              {unit.technology ? (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Inherited product</dt>
                  <dd>{unit.technology}</dd>
                </div>
              ) : null}
              {unit.class ? (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Inherited class</dt>
                  <dd>{unit.class}</dd>
                </div>
              ) : null}
              {unit.solution_type ? (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Detected solution</dt>
                  <dd>{unit.solution_type.replaceAll("_", " ")}</dd>
                </div>
              ) : null}
              {unit.classification_reason ? (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Classification reason</dt>
                  <dd>{unit.classification_reason}</dd>
                </div>
              ) : null}
              {(unit.classification_warnings || []).length ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Review warning</dt>
                  <dd className="mt-1 text-amber-900 dark:text-amber-100">{unit.classification_warnings.join(" ")}</dd>
                </div>
              ) : null}
              {unit.section_title ? (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Section</dt>
                  <dd>{unit.section_title}</dd>
                </div>
              ) : null}
              {unit.source_start != null ? (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Source page</dt>
                  <dd>
                    {unit.source_start}
                    {unit.source_end && unit.source_end !== unit.source_start ? `–${unit.source_end}` : ""}
                  </dd>
                </div>
              ) : null}
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Text</dt>
                <dd className="whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-slate-200">
                  {displayText || "—"}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-slate-500">
              Evidence details are not available for {rowId}.
            </p>
          )}
        </div>
        {reviewRowId ? (
          <div className="border-t border-slate-200 p-4 dark:border-slate-700">
            <button
              type="button"
              className="w-full rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800"
              onClick={() => onOpenInReview?.(reviewRowId)}
            >
              Open in Compliance Review
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
